/**
 * Ad Video Agent — IdeaWorks v5.0
 * Einstein's 5-Beat Formula: Innovation Hook → Imagine → Funny Analogy → Value Reveal → CTA
 * Custom character upload · OpenAI (GPT-4o) primary · OpenRouter fallback
 * HeyGen AI Presenter · Sora 2 video generation (up to 20s)
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Video, Sparkles, RefreshCw, Play, Zap, CheckCircle,
  AlertCircle, Clock, Copy, Check, Wand2, Upload, Plus, X,
  Brain, Globe, Trash2, User, Film, ChevronDown, ChevronUp,
  Lightbulb, Target, MessageCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PLATFORMS = ['TikTok', 'Instagram Reels', 'YouTube Shorts', 'LinkedIn', 'Reels (Meta)', 'Twitter/X'];

const BEAT_LABELS = {
  innovation_hook: { label: 'Innovation Hook', color: '#f97316', desc: '0-3s: The scroll-stopper' },
  imagine: { label: 'Imagine...', color: '#a68eff', desc: '3-8s: Dream state' },
  funny_analogy: { label: 'Funny Analogy', color: '#3dffa0', desc: '8-13s: The memorable moment' },
  value_reveal: { label: 'Value Reveal', color: '#60a5fa', desc: '13-17s: The "therefore"' },
  cta: { label: 'CTA', color: '#fe2c55', desc: '17-20s: The action' },
};

function BeatBadge({ beat }) {
  const info = BEAT_LABELS[beat] || { label: beat, color: '#888', desc: '' };
  return (
    <span className="text-[9px] font-black px-1.5 py-0.5 rounded flex-shrink-0"
      style={{ background: `${info.color}25`, color: info.color, border: `1px solid ${info.color}40` }}>
      {info.label}
    </span>
  );
}

function SceneCard({ scene, charColor }) {
  const beat = BEAT_LABELS[scene.beat] || { color: charColor };
  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${beat.color}20` }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0"
          style={{ background: beat.color + '30', color: beat.color }}>
          {scene.scene_number}
        </span>
        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{scene.duration_seconds}s</span>
        {scene.beat && <BeatBadge beat={scene.beat} />}
        {scene.text_overlay && (
          <span className="text-[9px] font-black px-1.5 py-0.5 rounded ml-auto max-w-[120px] truncate"
            style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}>
            {scene.text_overlay}
          </span>
        )}
      </div>
      <p className="text-xs font-semibold text-white mb-1 leading-snug">"{scene.dialogue}"</p>
      {scene.visual && (
        <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.3)' }}>{scene.visual}</p>
      )}
    </div>
  );
}

function CharacterCard({ char, selected, onSelect, image, onGenImage, generatingImage, onDelete }) {
  return (
    <div onClick={() => onSelect(char.id)}
      className="relative rounded-xl p-3 cursor-pointer transition-all group"
      style={{
        background: selected ? `${char.color}15` : 'rgba(255,255,255,0.02)',
        border: `1.5px solid ${selected ? char.color + '60' : 'rgba(255,255,255,0.07)'}`,
        boxShadow: selected ? `0 0 16px ${char.color}20` : 'none',
      }}>
      {selected && <div className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{ background: char.color }} />}
      {char.is_custom && onDelete && (
        <button onClick={e => { e.stopPropagation(); onDelete(char.id); }}
          className="absolute top-1.5 right-1.5 p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:opacity-100"
          style={{ background: 'rgba(235,87,87,0.15)', color: '#eb5757' }}>
          <X size={10} />
        </button>
      )}

      {/* Character image */}
      <div className="w-full aspect-square rounded-lg mb-2 overflow-hidden flex items-center justify-center relative"
        style={{ background: `${char.color}10`, border: `1px dashed ${char.color}30` }}>
        {(image || char.image_b64) ? (
          <img src={`data:image/png;base64,${image || char.image_b64}`} alt={char.name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-3xl">{char.emoji || '👤'}</div>
        )}
        {!char.is_custom && (
          <button onClick={e => { e.stopPropagation(); onGenImage(char.id); }}
            disabled={generatingImage === char.id}
            className="absolute bottom-1 right-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold opacity-0 group-hover:opacity-100 transition-all"
            style={{ background: `${char.color}40`, color: char.color }}>
            {generatingImage === char.id ? <RefreshCw size={7} className="animate-spin" /> : <Wand2 size={7} />}
            AI
          </button>
        )}
      </div>

      <div className="text-[11px] font-bold leading-tight" style={{ color: char.color }}>{char.name}</div>
      {char.is_custom && <div className="text-[9px] mt-0.5 text-[rgba(255,255,255,0.35)]">Custom</div>}
      {selected && char.catchphrase && (
        <div className="text-[9px] mt-1 italic leading-snug" style={{ color: 'rgba(255,255,255,0.4)' }}>
          "{char.catchphrase}"
        </div>
      )}
    </div>
  );
}

function UploadCharacterModal({ onSave, onClose }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result.split(',')[1]); // get base64
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/advideo/custom-characters`, {
        name: name.trim(),
        description: description.trim(),
        image_b64: imagePreview || '',
      });
      onSave(data.character);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to create character');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6 space-y-4"
        style={{ background: '#0a0a16', border: '1px solid rgba(124,94,250,0.3)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div className="flex items-center justify-between">
          <div className="text-sm font-black text-white">Create Custom Character</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: 'rgba(255,255,255,0.4)' }}>
            <X size={14} />
          </button>
        </div>

        {/* Image upload */}
        <div
          onClick={() => fileRef.current?.click()}
          className="w-full aspect-video rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer transition-all hover:opacity-80"
          style={{ background: 'rgba(124,94,250,0.07)', border: '1px dashed rgba(124,94,250,0.3)' }}>
          {imagePreview ? (
            <img src={`data:image/png;base64,${imagePreview}`} alt="preview" className="w-full h-full object-cover rounded-xl" />
          ) : (
            <>
              <Upload size={20} style={{ color: '#a68eff' }} />
              <div className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Upload character image<br /><span className="text-[10px]">PNG / JPG — optional</span>
              </div>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />
        </div>

        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Character Name *</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Alex the Analyst"
            className="w-full px-3 py-2 rounded-xl text-sm text-white outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} />
        </div>

        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Personality & Style</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            placeholder="e.g. Sharp financial analyst who makes complex numbers simple and funny. Known for 'The numbers don't lie...' openings."
            rows={3}
            className="w-full px-3 py-2 rounded-xl text-sm text-white outline-none resize-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }} />
          <div className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>
            Einstein will auto-generate personality brief, catchphrase & script rules from this description.
          </div>
        </div>

        <button onClick={save} disabled={!name.trim() || saving}
          className="w-full py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
          {saving ? <><RefreshCw size={13} className="animate-spin inline mr-2" />Creating...</> : <><Wand2 size={13} className="inline mr-2" />Create Character</>}
        </button>
      </div>
    </div>
  );
}

export default function AdVideoAgent() {
  const [allCharacters, setAllCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState('einstein');
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('TikTok');
  const [duration, setDuration] = useState(20);
  const [tone, setTone] = useState('witty');
  const [videoType, setVideoType] = useState('social_clip');

  const [script, setScript] = useState(null);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const [charImages, setCharImages] = useState({});
  const [generatingImage, setGeneratingImage] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [heygenJob, setHeygenJob] = useState(null);
  const [generatingHeygen, setGeneratingHeygen] = useState(false);
  const [heygenStatus, setHeygenStatus] = useState(null);
  const pollRef = useRef(null);

  const [soraJob, setSoraJob] = useState(null);
  const [generatingSora, setGeneratingSora] = useState(false);
  const [soraPrompt, setSoraPrompt] = useState('');

  const [profile, setProfile] = useState(null);
  const [directorRules, setDirectorRules] = useState(null);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [charsRes, jobsRes, profileRes] = await Promise.all([
        axios.get(`${API}/api/advideo/all-characters`),
        axios.get(`${API}/api/advideo/history`),
        axios.get(`${API}/api/onboarding/profile`).catch(() => ({ data: {} })),
      ]);
      setAllCharacters(charsRes.data.characters || []);
      setJobs(jobsRes.data.jobs || []);
      if (profileRes.data?.profile) setProfile(profileRes.data.profile);
    } catch {}
  };

  const generateCharImage = async (charId) => {
    setGeneratingImage(charId);
    try {
      const { data } = await axios.post(`${API}/api/advideo/generate-character-image`, { character_id: charId });
      if (data.image_b64) setCharImages(prev => ({ ...prev, [charId]: data.image_b64 }));
    } catch (e) {
      alert('Image generation failed: ' + (e?.response?.data?.detail || e.message));
    }
    setGeneratingImage(null);
  };

  const deleteCustomChar = async (charId) => {
    try {
      await axios.delete(`${API}/api/advideo/custom-characters/${charId}`);
      setAllCharacters(prev => prev.filter(c => c.id !== charId));
      if (selectedChar === charId) setSelectedChar('einstein');
    } catch {}
  };

  const generateScript = async () => {
    if (!topic.trim()) return;
    setGeneratingScript(true);
    setScript(null);
    setSoraJob(null);
    try {
      const { data } = await axios.post(`${API}/api/advideo/generate-script`, {
        topic, character_id: selectedChar,
        platform: platform.toLowerCase().replace(/[\s/]+/g, '_'),
        video_type: videoType, tone, duration,
      });
      setScript(data);
      if (data?.storyboard?.hook) {
        setSoraPrompt(data.storyboard.hook + '. ' + (data.storyboard.scenes?.[0]?.visual || ''));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Script generation failed');
    }
    setGeneratingScript(false);
  };

  const loadDirectorRules = async () => {
    const char = allCharacters.find(c => c.id === selectedChar);
    try {
      const { data } = await axios.post(`${API}/api/advideo/generate-director-rules`, {
        character_name: char?.name || 'Einstein',
        character_description: char?.personality || char?.description || '',
      });
      setDirectorRules(data.rules);
      setShowRules(true);
    } catch {}
  };

  const generateHeyGenVideo = async () => {
    if (!script?.storyboard) return;
    setGeneratingHeygen(true);
    setHeygenJob(null);
    setHeygenStatus(null);
    try {
      const scriptText = script.storyboard?.scenes?.map(s => s.dialogue).join(' ') || topic;
      const { data } = await axios.post(`${API}/api/advideo/generate-heygen-video`, {
        character_id: selectedChar, script: scriptText, duration,
      });
      setHeygenJob(data);
      if (data.video_id) startPoll(data.video_id);
    } catch (e) {
      alert(e?.response?.data?.detail || 'HeyGen video failed');
    }
    setGeneratingHeygen(false);
  };

  const generateSoraVideo = async () => {
    if (!soraPrompt.trim()) return;
    setGeneratingSora(true);
    setSoraJob(null);
    try {
      const { data } = await axios.post(`${API}/api/advideo/generate-sora-video`, {
        character_id: selectedChar, prompt: soraPrompt, duration, resolution: '1080p',
      });
      setSoraJob(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Sora 2 generation failed');
    }
    setGeneratingSora(false);
  };

  const startPoll = (videoId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/advideo/heygen-status/${videoId}`);
        setHeygenStatus(data);
        if (data.status === 'completed' || data.status === 'failed') clearInterval(pollRef.current);
      } catch { clearInterval(pollRef.current); }
    }, 5000);
  };

  const copyScript = () => {
    if (!script?.storyboard) return;
    const sb = script.storyboard;
    const text = [
      `HOOK: ${sb.hook}`,
      sb.beat2_imagine ? `IMAGINE: ${sb.beat2_imagine}` : '',
      sb.beat3_analogy ? `ANALOGY: ${sb.beat3_analogy}` : '',
      sb.beat4_reveal ? `REVEAL: ${sb.beat4_reveal}` : '',
      '',
      ...(sb.scenes || []).map(s => `[Scene ${s.scene_number} - ${s.duration_seconds}s]: "${s.dialogue}"`),
      '',
      `CTA: ${sb.cta_text} — ${sb.cta_url}`,
      `CAPTION: ${sb.caption}`,
      `HASHTAGS: ${(sb.hashtags || []).join(' ')}`,
    ].filter(l => l !== undefined).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const char = allCharacters.find(c => c.id === selectedChar) || allCharacters[0] || { color: '#a68eff', emoji: '🧬', name: 'Einstein' };

  return (
    <div className="h-full flex overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* LEFT: Config panel */}
      <div className="flex flex-col overflow-hidden flex-shrink-0" style={{ width: 300, borderRight: '1px solid var(--llmc-border)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ background: 'rgba(10,10,20,0.97)', borderBottom: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #f97316, #fe2c55)' }}>
              <Film size={15} className="text-white" />
            </div>
            <div>
              <div className="font-black text-white text-sm">Ad Video Agent</div>
              <div className="text-[9px]" style={{ color: 'var(--llmc-text-muted)' }}>Einstein's 5-Beat Formula</div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Profile context */}
          {profile?.business_name && (
            <div className="rounded-xl p-2.5 flex items-start gap-2"
              style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)' }}>
              <Brain size={11} style={{ color: '#3dffa0', flexShrink: 0, marginTop: 1 }} />
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: '#3dffa0' }}>Context Loaded</div>
                <div className="text-[10px] text-white">{profile.business_name}</div>
                <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{profile.tone} · {profile.style}</div>
              </div>
            </div>
          )}

          {/* Character selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Character</label>
              <button onClick={() => setShowUpload(true)} data-testid="add-custom-char-btn"
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-bold transition-all hover:opacity-80"
                style={{ background: 'rgba(124,94,250,0.12)', border: '1px solid rgba(124,94,250,0.3)', color: '#a68eff' }}>
                <Plus size={9} /> Upload Custom
              </button>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {allCharacters.map(c => (
                <CharacterCard key={c.id} char={c} selected={selectedChar === c.id}
                  onSelect={setSelectedChar} image={charImages[c.id]}
                  onGenImage={generateCharImage} generatingImage={generatingImage}
                  onDelete={c.is_custom ? deleteCustomChar : null} />
              ))}
            </div>
          </div>

          {/* Topic */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Topic *</label>
            <textarea value={topic} onChange={e => setTopic(e.target.value)}
              data-testid="advideo-topic-input"
              placeholder={selectedChar === 'einstein'
                ? "e.g. How AI is transforming cybersecurity in 2026"
                : `What should ${char?.name || 'your character'} talk about?`}
              rows={3}
              className="w-full px-3 py-2 rounded-xl text-xs text-white outline-none resize-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generateScript(); }}
            />
          </div>

          {/* Platform */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Platform</label>
            <div className="grid grid-cols-2 gap-1">
              {DEFAULT_PLATFORMS.map(p => (
                <button key={p} onClick={() => setPlatform(p)} data-testid={`platform-${p.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="px-2 py-1.5 rounded-lg text-[10px] font-semibold text-left transition-all truncate"
                  style={{
                    background: platform === p ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${platform === p ? 'rgba(249,115,22,0.5)' : 'rgba(255,255,255,0.07)'}`,
                    color: platform === p ? '#fb923c' : 'rgba(255,255,255,0.4)',
                  }}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Duration + Tone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Duration</label>
              <div className="flex gap-1">
                {[15, 20].map(d => (
                  <button key={d} onClick={() => setDuration(d)}
                    className="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all"
                    style={{
                      background: duration === d ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${duration === d ? 'rgba(249,115,22,0.5)' : 'rgba(255,255,255,0.08)'}`,
                      color: duration === d ? '#f97316' : 'rgba(255,255,255,0.35)',
                    }}>
                    {d}s
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Tone</label>
              <select value={tone} onChange={e => setTone(e.target.value)} data-testid="tone-select"
                className="w-full px-2 py-1.5 rounded-xl text-xs text-white outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {['witty', 'educational', 'inspiring', 'shocking'].map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Director rules button */}
          <button onClick={loadDirectorRules}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: 'rgba(124,94,250,0.08)', border: '1px solid rgba(124,94,250,0.2)', color: '#a68eff' }}>
            <Lightbulb size={11} /> View Character Rules & Guardrails
          </button>
        </div>

        {/* Generate button */}
        <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid var(--llmc-border)' }}>
          <button onClick={generateScript} disabled={!topic.trim() || generatingScript}
            data-testid="generate-script-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-40"
            style={{ background: generatingScript ? 'rgba(249,115,22,0.2)' : `linear-gradient(135deg, ${char.color || '#f97316'}, ${char.color || '#f97316'}bb)`, color: '#fff' }}>
            {generatingScript ? <><RefreshCw size={14} className="animate-spin" />Generating...</> : <><Sparkles size={14} />Generate Script</>}
          </button>
          <div className="text-[10px] text-center mt-1.5" style={{ color: 'rgba(255,255,255,0.2)' }}>
            {selectedChar === 'einstein' ? "Einstein's 5-Beat Formula" : `${char?.name || ''}'s adapted formula`} · ⌘+Enter
          </div>
        </div>
      </div>

      {/* RIGHT: Script preview + video generation */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3 flex-shrink-0"
          style={{ background: 'rgba(10,10,20,0.97)', borderBottom: '1px solid var(--llmc-border)' }}>
          <Play size={15} style={{ color: char.color || '#f97316' }} />
          <span className="font-bold text-sm text-white">Script Preview</span>
          {script?.formula && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
              style={{ background: 'rgba(249,115,22,0.1)', color: '#fb923c', border: '1px solid rgba(249,115,22,0.2)' }}>
              {script.formula === 'einstein_5beat' ? 'Einstein 5-Beat' : 'Character Adapted'}
            </span>
          )}
          {script?.storyboard && (
            <div className="ml-auto flex items-center gap-2">
              <button onClick={copyScript}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs transition-all hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
                {copied ? <Check size={11} style={{ color: '#3dffa0' }} /> : <Copy size={11} />}
                {copied ? 'Copied' : 'Copy All'}
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {generatingScript && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center animate-pulse"
                style={{ background: `${char.color || '#f97316'}20` }}>
                <Wand2 size={22} style={{ color: char.color || '#f97316' }} />
              </div>
              <div className="text-xs font-semibold text-white">
                {selectedChar === 'einstein' ? 'Einstein is crafting the 5-beat formula...' : `${char?.name} is scripting...`}
              </div>
              <div className="space-y-1 text-center">
                {Object.values(BEAT_LABELS).map((b, i) => (
                  <div key={b.label} className="text-[10px] animate-pulse" style={{ color: b.color, animationDelay: `${i * 0.3}s` }}>
                    {b.label} — {b.desc}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Director Rules Panel */}
          {showRules && directorRules && (
            <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(124,94,250,0.3)' }}>
              <button onClick={() => setShowRules(false)}
                className="w-full flex items-center justify-between px-4 py-3"
                style={{ background: 'rgba(124,94,250,0.08)' }}>
                <div className="flex items-center gap-2">
                  <Lightbulb size={13} style={{ color: '#a68eff' }} />
                  <span className="text-xs font-bold text-white">Character Rules & Guardrails</span>
                </div>
                <ChevronUp size={13} style={{ color: 'rgba(255,255,255,0.4)' }} />
              </button>
              <div className="p-4 space-y-3" style={{ background: 'rgba(124,94,250,0.04)' }}>
                {directorRules.system_prompt && (
                  <div>
                    <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#a68eff' }}>System Prompt</div>
                    <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{directorRules.system_prompt}</div>
                  </div>
                )}
                {directorRules.guardrails?.length > 0 && (
                  <div>
                    <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#3dffa0' }}>Guardrails</div>
                    <ul className="space-y-1">
                      {directorRules.guardrails.map((r, i) => (
                        <li key={i} className="text-[11px] flex items-start gap-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          <span style={{ color: '#3dffa0' }}>•</span> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {directorRules.example_hook && (
                  <div className="rounded-xl p-3" style={{ background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.2)' }}>
                    <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#f97316' }}>Example Hook</div>
                    <div className="text-xs italic" style={{ color: 'rgba(255,255,255,0.6)' }}>"{directorRules.example_hook}"</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {script?.storyboard && !generatingScript && (
            <div className="space-y-4">
              {/* Character + topic header */}
              <div className="flex items-center gap-3 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{ background: `${char.color || '#f97316'}15`, border: `1px solid ${char.color || '#f97316'}40` }}>
                  {char.emoji || '🎬'}
                </div>
                <div>
                  <div className="text-sm font-bold" style={{ color: char.color || '#f97316' }}>{char.name}</div>
                  <div className="text-xs text-white font-semibold">{script.topic}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{platform} · {duration}s</div>
                  <div className="text-[10px]" style={{ color: char.color || '#f97316' }}>{script.storyboard.viral_hook_type?.replace(/_/g, ' ')}</div>
                </div>
              </div>

              {/* 5-Beat summary cards */}
              {selectedChar === 'einstein' && (script.storyboard.beat2_imagine || script.storyboard.beat3_analogy) && (
                <div className="grid grid-cols-2 gap-2">
                  {script.storyboard.hook && (
                    <div className="rounded-xl p-3" style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)' }}>
                      <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#f97316' }}>Beat 1 — Hook</div>
                      <div className="text-xs leading-snug text-white">"{script.storyboard.hook}"</div>
                    </div>
                  )}
                  {script.storyboard.beat2_imagine && (
                    <div className="rounded-xl p-3" style={{ background: 'rgba(166,142,255,0.06)', border: '1px solid rgba(166,142,255,0.2)' }}>
                      <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#a68eff' }}>Beat 2 — Imagine</div>
                      <div className="text-xs leading-snug text-white">"{script.storyboard.beat2_imagine}"</div>
                    </div>
                  )}
                  {script.storyboard.beat3_analogy && (
                    <div className="rounded-xl p-3" style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)' }}>
                      <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#3dffa0' }}>Beat 3 — Analogy</div>
                      <div className="text-xs leading-snug text-white">"{script.storyboard.beat3_analogy}"</div>
                    </div>
                  )}
                  {script.storyboard.beat4_reveal && (
                    <div className="rounded-xl p-3" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}>
                      <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#60a5fa' }}>Beat 4 — Reveal</div>
                      <div className="text-xs leading-snug text-white">"{script.storyboard.beat4_reveal}"</div>
                    </div>
                  )}
                </div>
              )}

              {/* Scenes */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--llmc-text-muted)' }}>
                  Scenes ({script.storyboard.scenes?.length || 0} · {script.storyboard.total_seconds}s)
                </div>
                <div className="space-y-2">
                  {(script.storyboard.scenes || []).map((s, i) => (
                    <SceneCard key={i} scene={s} charColor={char.color || '#f97316'} />
                  ))}
                </div>
              </div>

              {/* CTA + Caption + Hashtags */}
              <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(254,44,85,0.06)', border: '1px solid rgba(254,44,85,0.2)' }}>
                <div className="flex items-center gap-2">
                  <Target size={12} style={{ color: '#fe2c55' }} />
                  <span className="text-xs font-bold" style={{ color: '#fe2c55' }}>CTA: {script.storyboard.cta_text}</span>
                  {script.storyboard.cta_url && (
                    <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{script.storyboard.cta_url}</span>
                  )}
                </div>
                {script.storyboard.caption && (
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{script.storyboard.caption}</div>
                )}
                <div className="flex flex-wrap gap-1">
                  {(script.storyboard.hashtags || []).map((h, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>{h}</span>
                  ))}
                </div>
              </div>

              {/* Video generation actions */}
              <div className="space-y-3">
                <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Generate Video</div>

                {/* HeyGen */}
                <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User size={13} style={{ color: '#60a5fa' }} />
                      <span className="text-xs font-bold" style={{ color: '#60a5fa' }}>HeyGen AI Presenter</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa' }}>Lip-sync</span>
                    </div>
                    <button onClick={generateHeyGenVideo} disabled={generatingHeygen}
                      data-testid="generate-heygen-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all hover:opacity-80 disabled:opacity-40"
                      style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.4)', color: '#60a5fa' }}>
                      {generatingHeygen ? <RefreshCw size={11} className="animate-spin" /> : <Video size={11} />}
                      {generatingHeygen ? 'Generating...' : 'Generate'}
                    </button>
                  </div>
                  {heygenJob && (
                    <div className="flex items-center gap-2 text-xs">
                      {heygenStatus?.status === 'completed' ? <CheckCircle size={13} style={{ color: '#3dffa0' }} /> :
                        heygenStatus?.status === 'failed' ? <AlertCircle size={13} style={{ color: '#f97316' }} /> :
                          <Clock size={13} className="animate-pulse" style={{ color: '#60a5fa' }} />}
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {heygenStatus?.status === 'completed' ? 'Video ready!' : heygenStatus?.status === 'failed' ? 'Failed' : 'Rendering...'}
                      </span>
                    </div>
                  )}
                  {heygenStatus?.video_url && (
                    <video controls className="w-full rounded-xl" style={{ maxHeight: 250 }}>
                      <source src={heygenStatus.video_url} type="video/mp4" />
                    </video>
                  )}
                </div>

                {/* Sora 2 */}
                <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(254,44,85,0.06)', border: '1px solid rgba(254,44,85,0.2)' }}>
                  <div className="flex items-center gap-2">
                    <Zap size={13} style={{ color: '#fe2c55' }} />
                    <span className="text-xs font-bold" style={{ color: '#fe2c55' }}>Sora 2 Video Generation</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(254,44,85,0.12)', color: '#fe2c55' }}>up to {duration}s</span>
                  </div>
                  <textarea value={soraPrompt} onChange={e => setSoraPrompt(e.target.value)}
                    data-testid="sora-prompt-input"
                    rows={2}
                    className="w-full resize-none text-xs outline-none rounded-lg px-3 py-2"
                    style={{ color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(254,44,85,0.2)', background: 'rgba(254,44,85,0.04)', minHeight: 56 }}
                    placeholder="Visual scene description for Sora 2..." />
                  <button onClick={generateSoraVideo} disabled={generatingSora || !soraPrompt.trim()}
                    data-testid="generate-sora-btn"
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                    style={{ background: 'rgba(254,44,85,0.15)', border: '1px solid rgba(254,44,85,0.4)', color: '#fe2c55' }}>
                    {generatingSora ? <><RefreshCw size={11} className="animate-spin" />Submitting...</> : <><Play size={11} />Generate Sora 2 ({duration}s)</>}
                  </button>
                  {soraJob && (
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {soraJob.status === 'processing' ? 'Rendering (2-5 min)...' : `Status: ${soraJob.status}`}
                    </div>
                  )}
                </div>
              </div>

              {/* History toggle */}
              {jobs.length > 0 && (
                <div>
                  <button onClick={() => setShowHistory(h => !h)}
                    className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)' }}>
                    <div className="flex items-center gap-2">
                      <Film size={12} /> Video History ({jobs.length})
                    </div>
                    {showHistory ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                  {showHistory && (
                    <div className="mt-2 space-y-1.5">
                      {jobs.slice(0, 10).map(job => (
                        <div key={job.id} className="flex items-center gap-2 px-3 py-2 rounded-xl"
                          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <div className="text-base flex-shrink-0">{job.character_emoji || '🎬'}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-semibold text-white truncate">{job.topic}</div>
                            <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{job.character_name} · {job.platform}</div>
                          </div>
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                            style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0' }}>{job.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!script?.storyboard && !generatingScript && (
            <div className="flex flex-col items-center justify-center py-16 text-center space-y-5">
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center"
                style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.15)' }}>
                <Film size={32} style={{ color: 'rgba(249,115,22,0.4)' }} />
              </div>
              <div>
                <div className="text-sm font-bold text-white mb-2">Ready to create viral content?</div>
                <div className="text-xs max-w-xs mx-auto leading-relaxed" style={{ color: 'var(--llmc-text-muted)' }}>
                  Select Einstein (default) to use the proven 5-beat formula, or upload your own character for a personalized touch.
                </div>
              </div>
              {/* 5-beat formula preview */}
              <div className="grid grid-cols-1 gap-1.5 w-full max-w-xs">
                {Object.values(BEAT_LABELS).map(b => (
                  <div key={b.label} className="flex items-center gap-3 px-3 py-2 rounded-xl"
                    style={{ background: `${b.color}08`, border: `1px solid ${b.color}20` }}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: b.color }} />
                    <div className="text-left">
                      <div className="text-[10px] font-bold" style={{ color: b.color }}>{b.label}</div>
                      <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom character upload modal */}
      {showUpload && (
        <UploadCharacterModal
          onSave={(newChar) => {
            setAllCharacters(prev => [...prev, newChar]);
            setSelectedChar(newChar.id);
            setShowUpload(false);
          }}
          onClose={() => setShowUpload(false)}
        />
      )}
    </div>
  );
}
